import React, {useCallback, useEffect, useState} from 'react';
import {Badge, Box, Button, Divider} from '@mui/material';
import Typography from '@mui/material/Typography';
import ArrowUpwardOutlinedIcon from '@mui/icons-material/ArrowUpwardOutlined';
import ArrowDownwardOutlinedIcon from '@mui/icons-material/ArrowDownwardOutlined';
import {generateUUID} from '../../../../utility/uuidUtil';
import PropTypes from 'prop-types';
import {DragDropContext, Draggable, Droppable} from 'react-beautiful-dnd';
import {getComponentByType} from './FormBuilderComponents';
import {ComponentCategory, FORM_BUILDER_COMPONENTS, FormBuilderComponentTypes} from './ComponentList';
import ComponentBuilder from './Components/ComponentBuilder';
import DeleteIcon from '@mui/icons-material/DeleteOutlined';
import AdmicityDialog from '../../../../shared-components/AdmicityDialog';
import ShowFieldWhen from './ShowFieldWhen';
import VisibilityIcon from '@mui/icons-material/Visibility';
import IconButton from '@mui/material/IconButton';
import EditIcon from '@mui/icons-material/Edit';
import ConfirmRemoveDialog from './ConfirmRemoveDialog';
import {useGetRelatedFormQuestionAssignmentQuery} from '../../../../api/services/formAssignmentsService';
import Tooltip from '@mui/material/Tooltip';
import ImageWithConditionalRender from '../../../../shared-components/ImageWithConditionalRender';
import ErrorIcon from '@mui/icons-material/Error';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import {validateAge, validateEmail, validatePhoneNumber, validateUPN} from '../../../../utility/validationUtil';
import moment from 'moment/moment';
import Stack from '@mui/material/Stack';
import useNotification from '../../../../utility/hooks/useNotification';
import {stripHtmlTags} from '../../../../utility/stripHtmlTags';

export const availableComponents = [
    FormBuilderComponentTypes.Dropdown,
    FormBuilderComponentTypes.SingleChoice,
    FormBuilderComponentTypes.CheckboxQuestion,
    FormBuilderComponentTypes.MultipleChoice,
    FormBuilderComponentTypes.OfferStatusChoice,
    FormBuilderComponentTypes.BoarderStatusChoice,
    FormBuilderComponentTypes.IntakeChoice
];

const validators = {
    email: validateEmail,
    tel: validatePhoneNumber,
    upn: validateUPN,
    text: (value, isRequired) => {
        const isValid = !isRequired || (value != null && value !== '');

        return {
            isValid,
            message: isValid ? '' : 'This field is required'
        };
    },
};

const validateField = (field, submitData, schema, validateOnSubmit = false) => {
    if (field.relates) {
        const related = schema.find(e => e.id === field.relates.relatesTo);
        const relatedValue = submitData?.[related.id];
        const conditionMet = Array.isArray(relatedValue)
            ? relatedValue.includes(field.relates.targetState)
            : relatedValue === field.relates.targetState;

        if (!conditionMet) {
            return {
                isValid: true,
                error: ''
            };
        }
    }

    switch (field.type) {
        case FormBuilderComponentTypes.TickBoxGrid:
        case FormBuilderComponentTypes.MultipleChoiceGrid: {
            if (!field.enableValidation) {
                return {
                    isValid: true,
                    error: ''
                };
            }

            const data = submitData?.[field.id];
            const gridMissing = !data || field.rows.some(r => !data[r.id] || data[r.id].length === 0);
            return gridMissing
                ? {
                    isValid: false,
                    error: 'Please pick at least one option'
                }
                : {
                    isValid: true,
                    error: ''
                };
        }
        case FormBuilderComponentTypes.CheckboxQuestion:
        case FormBuilderComponentTypes.SingleChoice:
        case FormBuilderComponentTypes.OfferStatusChoice:
        case FormBuilderComponentTypes.IntakeChoice:
        case FormBuilderComponentTypes.LookupDropdown:
        case FormBuilderComponentTypes.FileUploader: {
            if (!field.enableValidation) {
                return {
                    isValid: true,
                    error: ''
                };
            }
            const hasValue = !!submitData?.[field.id];
            return hasValue
                ? {
                    isValid: true,
                    error: ''
                }
                : {
                    isValid: false,
                    error: 'This field is required'
                };
        }
        case FormBuilderComponentTypes.MultiLineInput: {
            const inputs = field.inputs || [];
            const data = submitData?.[field.id];

            const fieldErrors = inputs.reduce((errors, input) => {
                if (!input.enableValidation) return errors;

                const value = data?.[input.id];
                if (!value || !value.trim()) {
                    errors[input.id] = 'This field is required';
                }

                return errors;
            }, {});

            const isValid = Object.keys(fieldErrors).length === 0;

            return {
                isValid,
                error: fieldErrors
            };
        }
        case FormBuilderComponentTypes.SingleText: {
            const data = submitData?.[field.id];
            const mustValidate = field.enableValidation || (data !== '' && data !== undefined);

            if (!mustValidate) {
                return {
                    isValid: true,
                    error: ''
                };
            }

            const validator = validators[field.inputType] ?? ((value) => validators.text(value, field.enableValidation));
            const {
                isValid,
                message
            } = validator(data, field.enableValidation);

            return {
                isValid,
                error: message
            };
        }
        case FormBuilderComponentTypes.UPN: {
            const data = submitData?.[field.id];
            const mustValidate = field.enableValidation || (data !== '' && data !== undefined);

            if (!mustValidate) {
                return {
                    isValid: true,
                    error: ''
                };
            }

            const {
                isValid,
                message
            } = validateUPN(data, field.enableValidation);
            return {
                isValid,
                error: message
            };
        }
        case FormBuilderComponentTypes.PreviousSchoolName: {
            if (!field.enableValidation) {
                return {
                    isValid: true,
                    error: ''
                };
            }
            const data = submitData?.[field.id];
            const hasValue = data != null && data !== '' && data !== undefined;
            return hasValue
                ? {
                    isValid: true,
                    error: ''
                }
                : {
                    isValid: false,
                    error: 'This field is required'
                };
        }
        case FormBuilderComponentTypes.PreviousSchoolEmail: {
            const data = submitData?.[field.id];
            const mustValidate = field.enableValidation || (data !== '' && data !== undefined);

            if (!mustValidate) {
                return {
                    isValid: true,
                    error: ''
                };
            }

            const {
                isValid,
                message
            } = validateEmail(data, field.enableValidation);
            return {
                isValid,
                error: message
            };
        }
        case FormBuilderComponentTypes.Address: {
            if (!validateOnSubmit || !field.enableValidation) {
                return {
                    isValid: true,
                    error: {}
                };
            }

            const data = submitData?.[field.id];
            const fieldErrors = {};

            ['town', 'postcode'].forEach(input => {
                const value = data[input];

                if (!value || value.trim() === '') {
                    fieldErrors[input] = 'This field is required';
                }
            });
            const isValid = Object.keys(fieldErrors).length === 0;

            return {
                isValid,
                error: fieldErrors
            };
        }
        case FormBuilderComponentTypes.NumericInputBox: {
            const raw = submitData?.[field.id];

            const isValidNumber = (s) => /^-?\d+(\.\d+)?$/.test(String(s).trim());

            if (raw === '' || raw === undefined || raw === null) {
                return field.enableValidation
                    ? {
                        isValid: false,
                        error: 'This field is required'
                    }
                    : {
                        isValid: true,
                        error: ''
                    };
            }

            if (!isValidNumber(raw)) {
                return {
                    isValid: false,
                    error: 'Enter a valid number, e.g. -3 or 0.03'
                };
            }

            return {
                isValid: true,
                error: ''
            };
        }
        case FormBuilderComponentTypes.LinearScale: {
            const empty = field.enableValidation && (submitData?.[field.id] === null || submitData?.[field.id] === undefined || submitData[field.id] === '');
            return empty
                ? {
                    isValid: false,
                    error: 'This field is required'
                }
                : {
                    isValid: true,
                    error: ''
                };
        }
        case FormBuilderComponentTypes.QuestionGrid: {
            const data = submitData?.[field.id];
            const fieldErrors = {};
            let hasError = false;

            const isMultiColumn = field.columns && field.columns.length > 0;

            field.inputs?.forEach(input => {
                if (input.required) {
                    const inputValue = data?.[input.id];

                    if (isMultiColumn) {
                        const inputErrors = {};
                        let inputHasError = false;

                        field.columns?.forEach(column => {
                            const columnValue = inputValue?.[column.id];
                            if (!columnValue || (typeof columnValue === 'string' && columnValue.trim() === '')) {
                                inputErrors[column.id] = 'This field is required';
                                inputHasError = true;
                            }
                        });

                        if (inputHasError) {
                            fieldErrors[input.id] = inputErrors;
                            hasError = true;
                        }
                    } else {
                        if (!inputValue || (typeof inputValue === 'string' && inputValue.trim() === '')) {
                            fieldErrors[input.id] = 'This field is required';
                            hasError = true;
                        }
                    }
                }
            });

            return {
                isValid: !hasError,
                error: hasError ? fieldErrors : ''
            };
        }
        case FormBuilderComponentTypes.Separator: {
            return {
                isValid: true,
                error: ''
            };
        }
        default: {
            const empty = field.enableValidation && (submitData?.[field.id] === undefined || submitData[field.id] === '');
            return empty
                ? {
                    isValid: false,
                    error: 'This field is required'
                }
                : {
                    isValid: true,
                    error: ''
                };
        }
    }
};

const formatAllowedList = (allowed) => {
    const uniq = Array.from(new Set(allowed)).sort((a, b) => a - b);
    if (uniq.length === 1) return `${uniq[0]}`;
    if (uniq.length === 2) return `${uniq[0]} or ${uniq[1]}`;
    return `${uniq.slice(0, -1).join(', ')} or ${uniq[uniq.length - 1]}`;
};

const validateSchemaField = (field) => {
    switch (field.type) {
        case FormBuilderComponentTypes.Date: {
            if (field.minAge == null) {
                return '';
            }

            return validateAge(Number(field.minAge));
        }
        case FormBuilderComponentTypes.DateTime: {
            const minDate = field.minDateTime;
            const maxDate = field.maxDateTime;

            if (minDate && maxDate) {
                return moment(minDate).isAfter(moment(maxDate))
                    ? 'Min date time cannot be after max date time'
                    : '';
            }

            return '';
        }
        case FormBuilderComponentTypes.OfferStatusChoice: {
            if (field && (!field.options?.length ||
                !field.options?.every(o => o.value !== '' && o.value != null))) {
                return 'Please configure the statuses for Application Status Choice component';
            }

            return '';
        }
        case FormBuilderComponentTypes.LookupDropdown: {
            if (field && (!field.lookupType ||
                field.lookupType === '')) {
                return 'Please select a lookup type for the Lookup Dropdown component';
            }

            return '';
        }
        case FormBuilderComponentTypes.IntakeChoice: {
            if (field && (!field.options?.length ||
                !field.options?.every(o => o.value !== '' && o.value != null))) {
                return 'Please configure the intakes for Intake Choice component';
            }

            if (field && field.applicationStatusId == null) {
                return 'Please Please select an application status to assign on form submission';
            }

            return '';
        }
        case FormBuilderComponentTypes.BoarderStatusChoice: {
            if (field && (!field.options?.length ||
                !field.options?.every(o => o.value !== '' && o.value != null))) {
                return 'Please configure the statuses for Boarder Status Choice component';
            }

            return '';
        }
        case FormBuilderComponentTypes.NumericInputBox: {
            const k = field.fieldKey ?? field.key;
            const allowed = field.validation?.allowedValues;

            if (k && k.toLowerCase().includes('priority')) {
                const allowedValues = Array.isArray(allowed) && allowed.length > 0 ? allowed : [1, 2];

                const n = Number(field.priority);
                const valid = Number.isInteger(n) && allowedValues.includes(n);

                if (!valid) {
                    const label = formatAllowedList(allowedValues);

                    if (k === 'parent1Priority') return `Please set Parent 1 priority to a whole number (${label}).`;
                    if (k === 'parent2Priority') return `Please set Parent 2 priority to a whole number (${label}).`;
                    return `Please set priority to a whole number (${label}).`;
                }
            }

            return '';
        }
        case FormBuilderComponentTypes.QuestionGrid: {
            if (field && (!field.inputs?.length || field.inputs.length === 0)) {
                return 'Please add at least one question to the Question Grid component';
            }

            if (field && field.inputs?.some(input => !input.title || input.title.trim() === '')) {
                return 'Please fill in all question texts in the Question Grid component';
            }

            return '';
        }
        case FormBuilderComponentTypes.Separator: {
            return '';
        }
        default: {
            return '';
        }
    }
};

const validateSchema = (schema) => {
    const errors = {};
    let hasError = false;

    schema.forEach(f => {
        const validationResult = validateSchemaField(f);

        errors[f.id] = validationResult;

        if (!hasError && validationResult) {
            hasError = true;
        }
    });

    return errors;
};

const MAX_FILE_UPLOADER_COMPONENTS_COUNT = 10;

const formComponentsByCategory = FORM_BUILDER_COMPONENTS.reduce((acc, item) => {
    const {category} = item;
    acc[category] = acc[category] || [];
    acc[category].push(item);
    return acc;
}, {});

const FormBuilder = ({
                         schema,
                         schemaId,
                         onSchemaChange,
                         onSchemaValidationChange,
                         submitMode = false,
                         handleSubmit,
                         handleDownloadAttachment,
                         data,
                         readOnly,
                         isFormArchived,
                         placeholderValues,
                         accessLevel,
                         formId,
                         componentToEditIndex,
                         setComponentToEditIndex,
                         publicForm,
                         isCommunication,
                         audience,
                         recipientType,
                         formRequestId,
                         onEmailCheck
                     }) => {
    if (!schema) return <></>;

    const [previewMode, setPreviewMode] = useState(false);
    const [submitData, setSubmitData] = useState({});
    const [currentComponent, setCurrentComponent] = useState(undefined);
    const [relatedComponents, setRelatedComponents] = useState([]);
    const [componentToConfirmRemove, setComponentToConfirmRemove] = useState(undefined);
    const [formPlaceholders, setFormPlaceholders] = useState(undefined);
    const [errors, setErrors] = useState({});
    const [schemaErrors, setSchemaErrors] = useState({});
    const {data: relatedFormQuestionAssignData} = useGetRelatedFormQuestionAssignmentQuery(formId, {skip: submitMode || !formId});
    const {showErrorNotification} = useNotification();
    const schoolLogoComponent = schema?.find(x => x.type === 'schoolLogo');
    const [warnings, setWarnings] = useState({});
    const [drafts, setDrafts] = useState({});
    const setWarningFor = (componentId, message) => {
        if (!componentId) return;

        setWarnings(prev => {
            const next = message ?? '';
            if ((prev[componentId] ?? '') === next) return prev;
            return {
                ...prev,
                [componentId]: next
            };
        });
    };

    useEffect(() => {
        if (isFormArchived) {
            setPreviewMode(true);

            if (setComponentToEditIndex) {
                setComponentToEditIndex(undefined);
            }
        }
    }, [isFormArchived, setComponentToEditIndex]);

    useEffect(() => {
        setRelatedComponents(schema.filter(e => availableComponents.includes(e.type) && e.id !== currentComponent?.id));
    }, [schema]);

    useEffect(() => {
        if (submitMode) {
            setFormPlaceholders(placeholderValues);
        }
    }, [placeholderValues]);

    useEffect(() => {
        if (submitMode && data && Object.keys(data).length > 0) {
            setSubmitData({ ...data });
        }
    }, [submitMode, data]);

    useEffect(() => {
        const validation = !readOnly
            ? validateSchema(schema)
            : {};

        if (onSchemaValidationChange) {
            onSchemaValidationChange(validation);
        }

        setSchemaErrors(validation);
    }, [schemaId, schema]);

    const handleSetSchema = (newSchema) => {
        const validation = !readOnly
            ? validateSchema(newSchema)
            : {};

        onSchemaChange(newSchema);

        if (onSchemaValidationChange) {
            onSchemaValidationChange(validation);
        }

        setSchemaErrors(validation);
    };

    const handleChangeEdit = ({
                                  index,
                                  description,
                                  bookingLink,
                                  paymentLink,
                                  priority,
                                  validation,
                                  enableValidation,
                                  option,
                                  optionId,
                                  isAdd,
                                  isRemove,
                                  files,
                                  listKey,
                                  startFrom,
                                  endAt,
                                  startLabel,
                                  endLabel,
                                  formattedDescription,
                                  lookupType,
                                  minAge,
                                  minDateTime,
                                  maxDateTime,
                                  inputType,
                                  applicationStatusId,
                                  alignment
                              }) => {
        const newForm = [...schema];
        const updatedItem = structuredClone(newForm[index]);

        if (description !== undefined) {
            updatedItem.description = description;
        }
        if (formattedDescription !== undefined) {
            updatedItem.formattedDescription = formattedDescription;
        }
        if (bookingLink !== undefined) {
            updatedItem.bookingLink = bookingLink;
        }
        if (paymentLink !== undefined) {
            updatedItem.paymentLink = paymentLink;
        }
        if (priority === undefined || priority === '') {
            delete updatedItem.priority
        } else {
            updatedItem.priority = priority;
        }
        if (validation !== undefined) {
            updatedItem.validation = validation;
        }
        if (enableValidation !== undefined) {
            updatedItem.enableValidation = enableValidation;
        }
        if (option !== undefined && !listKey) {
            const optIndex = updatedItem.options.findIndex(e => e.id === optionId);
            if (optIndex !== -1) {
                const opt = {...updatedItem.options[optIndex]};
                opt.description = option.description;

                if (Object.prototype.hasOwnProperty.call(option, 'value')) {
                    opt.value = option.value;
                }
                const options = [...updatedItem.options];
                options[optIndex] = opt;
                updatedItem.options = options;
            }
        }
        if (isAdd && !listKey) {
            updatedItem.options = [
                ...updatedItem.options,
                {id: generateUUID()}
            ];
        }
        if (isRemove && !listKey) {
            const relatedComponent = schema.find(e => e.relates && e.relates.targetState === optionId);
            if (relatedComponent) {
                setComponentToConfirmRemove({
                    component: relatedComponent,
                    handleRemove: () => removeOptionModal(updatedItem.id, optionId)
                });
            } else {
                updatedItem.options = updatedItem.options.filter(e => e.id !== optionId);
            }
        }
        if (listKey) {
            if (!updatedItem[listKey]) {
                updatedItem[listKey] = [];
            }

            if (isRemove) {
                updatedItem[listKey] = updatedItem[listKey].filter(
                    (item, idx) => idx !== optionId && item.id !== optionId
                );
            } else if (isAdd) {
                const newOption =
                    typeof option === 'object' && option !== null
                        ? option
                        : {
                            title: '',
                            id: generateUUID()
                        };

                updatedItem[listKey] = [...updatedItem[listKey], newOption];
            } else {
                const itemIndex =
                    typeof optionId === 'number'
                        ? optionId
                        : updatedItem[listKey].findIndex((i) => i.id === optionId);

                if (itemIndex !== -1) {
                    const current = updatedItem[listKey][itemIndex];
                    updatedItem[listKey][itemIndex] =
                        typeof option === 'string'
                            ? {
                                ...current,
                                title: option
                            }
                            : {...current, ...option};
                }
            }
        }
        if (files !== undefined) {
            updatedItem.attachments = files;
        }
        if (startFrom !== undefined) {
            updatedItem.startFrom = startFrom;
        }
        if (endAt !== undefined) {
            updatedItem.endAt = endAt;
        }
        if (startLabel !== undefined) {
            updatedItem.startLabel = startLabel;
        }
        if (endLabel !== undefined) {
            updatedItem.endLabel = endLabel;
        }
        if (lookupType !== undefined) {
            updatedItem.lookupType = lookupType;
        }
        if (minAge !== undefined) {
            updatedItem.minAge = minAge;
        }
        if (minDateTime !== undefined) {
            updatedItem.minDateTime = minDateTime;
        }
        if (maxDateTime !== undefined) {
            updatedItem.maxDateTime = maxDateTime;
        }
        if (inputType !== undefined) {
            updatedItem.inputType = inputType;
        }
        if (applicationStatusId !== undefined) {
            updatedItem.applicationStatusId = applicationStatusId;
        }
        if (alignment !== undefined) {
            updatedItem.alignment = alignment;
        }

        newForm[index] = updatedItem;
        handleSetSchema(newForm);
    };

    const getDraftDescriptionHtml = useCallback((componentId, fallbackHtml) => {
        return Object.prototype.hasOwnProperty.call(drafts, componentId)
            ? drafts[componentId]
            : fallbackHtml;
    }, [drafts]);

    const setDraftDescriptionHtml = useCallback((componentId, html) => {
        setDrafts(prev => (prev[componentId] === html
            ? prev
            : {
                ...prev,
                [componentId]: html
            }));
    }, []);

    const commitDraftDescriptionToSchema = useCallback((componentId) => {
        const html = drafts[componentId];
        if (html === undefined) return;

        const index = schema.findIndex(x => x.id === componentId);
        if (index === -1) return;

        handleChangeEdit({
            index,
            description: stripHtmlTags(html),
            formattedDescription: html,
        });

        setDrafts(prev => {
            if (!Object.prototype.hasOwnProperty.call(prev, componentId)) return prev;
            const next = {...prev};
            delete next[componentId];
            return next;
        });
    }, [drafts, schema, handleChangeEdit]);

    const handleShowFieldWhen = (id, relatesTo, targetState) => {
        handleDialogClose();
        const newForm = JSON.parse(JSON.stringify(schema));
        let updatedComponent = newForm.find(e => e.id === id);

        if (relatesTo && targetState !== undefined) {
            updatedComponent.relates = {
                relatesTo,
                targetState
            };
        } else {
            delete updatedComponent.relates;
        }

        handleSetSchema(newForm);
    };

    const handleChangeSubmit = ({
                                    id,
                                    value,
                                    optionIndex,
                                    optionValue,
                                    radioValue
                                }) => {
        if (readOnly) return;

        setSubmitData(prev => {
            let newSubmitData = {...prev};

            if (value !== undefined) {
                newSubmitData[id] = value;
            } else if (optionIndex !== undefined && optionValue !== undefined) {
                if (!newSubmitData[id]) {
                    newSubmitData[id] = [];
                }

                newSubmitData[id] = optionValue ? [...newSubmitData[id], optionIndex] : newSubmitData[id].filter(o => o !== optionIndex);
                if (newSubmitData[id].length === 0) {
                    newSubmitData[id] = undefined;
                }
            } else if (radioValue !== undefined) {
                if (radioValue === null) {
                    delete newSubmitData[id]
                } else {
                    newSubmitData[id] = radioValue;
                }
            }

            schema?.forEach(field => {
                if (!checkIfVisible(field.relates, newSubmitData)) {
                    delete newSubmitData[field.id];
                }
            });

            const field = schema.find(field => field.id === id);

            if (field) {
                setErrors(prev => ({
                    ...prev,
                    [field.id]: validateField(field, newSubmitData, schema)
                }));
            }

            return newSubmitData;
        });
    };
    const applyDraftsToSchema = useCallback((baseSchema) => {
        if (!drafts || Object.keys(drafts).length === 0) return baseSchema;

        let changed = false;

        const next = baseSchema.map(item => {
            if (!Object.prototype.hasOwnProperty.call(drafts, item.id)) return item;

            const html = drafts[item.id] ?? '';
            const nextItem = {
                ...item,
                description: stripHtmlTags(html),
                formattedDescription: html
            };

            changed = true;
            return nextItem;
        });

        return changed ? next : baseSchema;
    }, [drafts]);

    const removeComponent = (componentId) => {
        const componentToRemove = schema.find(e => e.id === componentId);
        if (!componentToRemove) return;

        const related = schema.filter(e => e.relates?.relatesTo === componentId);

        if (related.length > 0) {
            setComponentToConfirmRemove({
                components: related,
                handleRemove: () => removeComponentModal(componentId)
            });
        } else {
            const hasDrafts = Object.keys(drafts).length > 0;
            const base = hasDrafts ? applyDraftsToSchema(schema) : schema;
            if (hasDrafts) setDrafts({});
            handleSetSchema(base.filter(e => e.id !== componentId));
        }
    };

    const moveComponent = (position, swapPosition) => {
        const hasDrafts = Object.keys(drafts).length > 0;
        const base = hasDrafts ? applyDraftsToSchema(schema) : schema;

        if (hasDrafts) setDrafts({});

        const newArray = [...base];
        const temp = newArray[position];
        newArray[position] = newArray[swapPosition];
        newArray[swapPosition] = temp;
        handleSetSchema(newArray);

        if (componentToEditIndex === position) {
            setComponentToEditIndex(swapPosition);
        } else if (componentToEditIndex === swapPosition) {
            setComponentToEditIndex(position);
        }
    };

    const insertComponent = (sourceIndex, destinationIndex) => {
        const hasDrafts = Object.keys(drafts).length > 0;
        const base = hasDrafts ? applyDraftsToSchema(schema) : schema;

        if (hasDrafts) setDrafts({});

        const newArray = [...base];
        const [movedItem] = newArray.splice(sourceIndex, 1);
        newArray.splice(destinationIndex, 0, movedItem);

        handleSetSchema(newArray);

        if (componentToEditIndex === sourceIndex) {
            setComponentToEditIndex(destinationIndex);
        } else if (
            componentToEditIndex > sourceIndex &&
            componentToEditIndex <= destinationIndex
        ) {
            setComponentToEditIndex(componentToEditIndex - 1);
        } else if (
            componentToEditIndex < sourceIndex &&
            componentToEditIndex >= destinationIndex
        ) {
            setComponentToEditIndex(componentToEditIndex + 1);
        }
    };

    const onDragEnd = (result) => {
        if (!result.destination) return;

        const sourceIndex = result.source.index;
        const destinationIndex = result.destination.index;

        if (sourceIndex === destinationIndex) return;

        insertComponent(sourceIndex, destinationIndex);
    };

    const removeComponentModal = (id) => {
        const hasDrafts = Object.keys(drafts).length > 0;
        const base = hasDrafts ? applyDraftsToSchema(schema) : schema;

        if (hasDrafts) setDrafts({});

        const newForm = JSON.parse(JSON.stringify(base));

        newForm.forEach(e => {
            if (e.relates && e.relates.relatesTo === id) {
                delete e.relates;
            }
        });

        handleSetSchema(newForm.filter((e) => e.id !== id));
        setComponentToConfirmRemove(undefined);
    };

    const removeOptionModal = (componentId, optionId) => {
        const hasDrafts = Object.keys(drafts).length > 0;
        const base = hasDrafts ? applyDraftsToSchema(schema) : schema;
        if (hasDrafts) setDrafts({});

        const newForm = JSON.parse(JSON.stringify(base));

        newForm.forEach(e => {
            if (e.relates && e.relates.targetState === optionId) delete e.relates;
        });

        const componentIndex = newForm.findIndex(e => e.id === componentId);
        if (componentIndex !== -1) {
            newForm[componentIndex].options =
                (newForm[componentIndex].options || []).filter(o => o.id !== optionId);
        }

        handleSetSchema(newForm);
        setComponentToConfirmRemove(undefined);
    };

    const addComponent = (type) => {
        const newComponent = {
            ...getComponentByType(type),
            enableValidation: type === 'signature',
            id: generateUUID()
        };

        const filteredSchema = schema.filter(component => component.type !== 'schoolLogo');

        handleSetSchema([
            ...filteredSchema,
            newComponent,
            ...schema.filter(component => component.type === 'schoolLogo')
        ]);
    };

    const handleDialogClose = () => {
        setCurrentComponent(undefined);
    };

    const buildEditRows = (f, index) => {
        const relatedComponent = relatedFormQuestionAssignData?.find(e => e.relatedComponentId === f.id);
        return (
            <Box display="flex" justifyContent="space-between" alignItems="center" py={1}>
                <Typography>{index + 1}. {f.label}</Typography>
                <Box display="flex" alignItems="center">
                    {!isFormArchived && (
                        <IconButton>
                            <EditIcon onClick={() => setComponentToEditIndex(index)}/>
                        </IconButton>
                    )}
                    {!isFormArchived && !isCommunication && !f.disableDelete && (
                        <Tooltip
                            title={relatedComponent
                                ? `This question cannot be deleted as it is currently used in the ${relatedComponent?.relatedFormName} form.`
                                : null}
                        >
                            <IconButton>
                                <DeleteIcon
                                    onClick={() => relatedComponent ? {} : removeComponent(f.id)}
                                />
                            </IconButton>
                        </Tooltip>
                    )}
                    {!isFormArchived &&
                        relatedComponents.filter(c => c.id !== f.id).length > 0 &&
                        !f.disableHide && (
                            <IconButton>
                                <VisibilityIcon
                                    color={f.relates ? 'primary' : ''}
                                    onClick={() => setCurrentComponent(f)}
                                />
                            </IconButton>
                        )}
                    {index !== 0 && !isFormArchived && (
                        <IconButton>
                            <ArrowUpwardOutlinedIcon onClick={() => moveComponent(index, index - 1)}/>
                        </IconButton>
                    )}
                    {index !== schema.length - 2 && !isFormArchived && (
                        <IconButton>
                            <ArrowDownwardOutlinedIcon onClick={() => moveComponent(index, index + 1)}/>
                        </IconButton>
                    )}
                </Box>
            </Box>
        );
    };

    const checkIfVisible = (relates, submitData) => {
        if (relates) {
            const relatedComponent = readOnly ? data[relates.relatesTo] : submitData[relates.relatesTo];
            return Array.isArray(relatedComponent)
                ? relatedComponent.includes(relates.targetState)
                : relatedComponent === relates.targetState || (relates.targetState === false && relatedComponent === undefined);
        }
        return true;
    };

    const onSubmit = () => {
        const validation = {};
        let isError = false;

        schema.forEach(f => {
            const validationResult = validateField(f, submitData, schema, true);

            if (!isError && !validationResult.isValid) {
                isError = true;
            }

            validation[f.id] = validationResult;
        });

        if (isError) {
            setErrors(validation);
            showErrorNotification('Please review the form and fill in all required fields')

            return;
        }

        const hiddenFieldIds = schema
            ?.filter(field => !checkIfVisible(field.relates, submitData))
            .map(field => field.id);
        const filteredData = Object.keys(submitData)
            .filter(key => !hiddenFieldIds.includes(key))
            .reduce((acc, key) => {
                acc[key] = submitData[key];
                return acc;
            }, {});

        handleSubmit(filteredData, JSON.stringify(formPlaceholders));
    };

    const renderComponentButton = ({
                                       type,
                                       description
                                   }) => {
        const isUploader = type === FormBuilderComponentTypes.FileUploader;
        const limitExceeded = isUploader && schema.filter(
            (x) => x.type === FormBuilderComponentTypes.FileUploader,
        ).length >= MAX_FILE_UPLOADER_COMPONENTS_COUNT;
        const disabled = isFormArchived || limitExceeded || isCommunication;

        const button = (
            <Button
                key={type}
                variant="outlined"
                disabled={disabled}
                onClick={() => {
                    addComponent(type);
                }}
            >
                {description}
            </Button>
        );

        if (!limitExceeded) return button;

        return (
            <Badge
                key={type}
                sx={{
                    '& .MuiBadge-badge': {
                        top: 5,
                        padding: '0 4px'
                    }
                }}
                badgeContent={
                    <Tooltip title="Maximum of 10 file upload fields allowed on the form">
                        <ErrorIcon color="warning"/>
                    </Tooltip>
                }
            >
                {button}
            </Badge>
        );
    };

    return (
        submitMode
            ? <Box display="flex" flexDirection="column" sx={{
                mx: publicForm
                    ? {
                        xs: 2,
                        sm: 0
                    }
                    : 0
            }}>
                {schema
                    ?.filter(e => checkIfVisible(e.relates, submitData) && e.showEditModeOnly !== true)
                    .map((f, index) => {

                        const hiddenTypes = ['intakeChoice', 'boarderStatusChoice'];

                        const validOptionCount = f.options?.filter(
                            o => o.value !== '' && o.value != null
                        ).length ?? 0;

                        const shouldHideVisually =
                            hiddenTypes.includes(f.type) && (validOptionCount === 0 || validOptionCount === 1);

                        if (f.type === 'schoolLogo') {
                            return null;
                        }

                        const isSeparator = f.type === 'separator';

                        const content = (
                            <ComponentBuilder
                                handleChangeSubmit={handleChangeSubmit}
                                handleDownloadAttachment={handleDownloadAttachment}
                                index={index}
                                readOnly={readOnly}
                                handleChangeEdit={handleChangeEdit}
                                previewMode={componentToEditIndex === index || previewMode}
                                formPreviewMode={previewMode}
                                field={f}
                                componentToEditIndex={componentToEditIndex}
                                data={data}
                                submitMode={submitMode}
                                submitData={submitData}
                                isFormArchived={isFormArchived}
                                accessLevel={accessLevel}
                                relatedComponent={relatedFormQuestionAssignData?.find(e => e.relatedComponentId === f.id)}
                                error={submitMode ? errors[f.id]?.error : schemaErrors[f.id]}
                                audience={audience}
                                recipientType={recipientType}
                                formRequestId={formRequestId}
                                onEmailCheck={onEmailCheck}
                                warnings={warnings}
                                setWarningFor={setWarningFor}
                                placeholderValues={formPlaceholders}
                                getDraftDescriptionHtml={getDraftDescriptionHtml}
                                setDraftDescriptionHtml={setDraftDescriptionHtml}
                                commitDraftDescriptionToSchema={commitDraftDescriptionToSchema}
                            />
                        );

                        return isSeparator
                            ? (
                                <Box key={`formItem-${f.id}`} sx={{
                                    my: 6,
                                }}>
                                    {content}
                                </Box>
                            )
                            : (
                                <Box
                                    key={`formItem-${f.id}`}
                                    sx={{
                                        my: 1,
                                        px: 3,
                                        py: 6,
                                        border: '1px solid #d1d5db',
                                        borderRadius: '8px',
                                        backgroundColor: '#ffffff',
                                        display: shouldHideVisually ? 'none' : 'block',
                                    }}
                                >
                                    {content}
                                </Box>
                            );
                    })
                }
                {
                    <Box>
                        {!readOnly && !previewMode && !publicForm && (
                            <Divider sx={{my: 1}}/>
                        )}
                        <Box display="flex" justifyContent="end">
                            {
                                readOnly || previewMode || isCommunication
                                    ? null
                                    : <Button
                                        sx={{
                                            mt: 1,
                                            mb: 2,
                                            backgroundColor: '#186BBE',
                                            boxShadow: 'none',
                                            width: {
                                                xs: '100%',
                                                sm: 'auto'
                                            },
                                            '&:hover': {
                                                backgroundColor: '#155FA7',
                                                boxShadow: 'none'
                                            }
                                        }}
                                        variant="contained"
                                        onClick={onSubmit}>
                                        Submit
                                    </Button>
                            }
                        </Box>
                    </Box>
                }
            </Box>
            : <Box onDoubleClick={() => submitMode
                ? () => {
                }
                : setComponentToEditIndex(undefined)}>
                <Box display={'flex'} justifyContent={'end'}>
                    {!submitMode && !isFormArchived && (
                        <Tooltip
                            title={componentToEditIndex >= 0
                                ? 'Preview is disabled while editing components. Double-click outside the component area to exit edit mode and enable the preview.'
                                : null}
                        >
            <span>
                <Button
                    disabled={componentToEditIndex >= 0}
                    onClick={() =>
                        componentToEditIndex >= 0
                            ? setComponentToEditIndex(undefined)
                            : setPreviewMode(!previewMode)
                    }
                    sx={{width: '100px'}}
                >
                    {previewMode ? 'Edit' : 'Preview'}
                </Button>
            </span>
                        </Tooltip>
                    )}
                </Box>

                <Box display={'flex'} fullWidth justifyContent={'center'}>
                    <Box
                        sx={{
                            flex: 2,
                            p: 2,
                            mx: previewMode ? '24%' : '22%',
                            mb: 2
                        }}
                    >
                        {schoolLogoComponent?.url && (
                            <Box textAlign="center" sx={{
                                mx: {
                                    xs: 2,
                                    sm: 0
                                },
                                my: 2,
                                px: 3,
                                py: 6,
                                border: '1px solid #d1d5db',
                                borderRadius: '8px',
                                backgroundColor: '#ffffff',
                            }}>
                                <ImageWithConditionalRender
                                    imageUrl={schoolLogoComponent.url}
                                    alt="School logo"
                                />
                            </Box>
                        )}
                        {!previewMode
                            ? <DragDropContext onDragEnd={onDragEnd}>
                                <Droppable droppableId="droppable">
                                    {(provided) => (
                                        <Box
                                            {...provided.droppableProps}
                                            ref={provided.innerRef}
                                        >
                                            {schema?.map((f, index) =>
                                                f.type !== 'schoolLogo'
                                                    ? (
                                                        <Draggable
                                                            key={f.id}
                                                            draggableId={String(f.id)}
                                                            index={index}
                                                            isDragDisabled={isFormArchived || isCommunication}
                                                        >
                                                            {(provided) => (
                                                                <Box
                                                                    ref={provided.innerRef}
                                                                    {...provided.draggableProps}
                                                                    {...provided.dragHandleProps}
                                                                >
                                                                    <Box
                                                                        key={`formItem-${f.id}`}
                                                                        sx={{
                                                                            mx: {
                                                                                xs: 2,
                                                                                sm: 0
                                                                            },
                                                                            my: 2,
                                                                            px: 3,
                                                                            py: 6,
                                                                            border: '1px solid #d1d5db',
                                                                            borderRadius: '8px',
                                                                            backgroundColor: '#ffffff',
                                                                            borderColor: schemaErrors[f.id] ? 'error.main' : '',
                                                                        }}
                                                                    >
                                                                        {buildEditRows(f, index)}
                                                                        <ComponentBuilder
                                                                            handleChangeSubmit={handleChangeSubmit}
                                                                            index={index}
                                                                            readOnly={readOnly}
                                                                            handleChangeEdit={handleChangeEdit}
                                                                            previewMode={componentToEditIndex !== index || previewMode}
                                                                            formPreviewMode={previewMode}
                                                                            field={f}
                                                                            componentToEditIndex={componentToEditIndex}
                                                                            data={data}
                                                                            submitMode={submitMode}
                                                                            submitData={submitData}
                                                                            isFormArchived={isFormArchived}
                                                                            accessLevel={accessLevel}
                                                                            relatedComponent={relatedFormQuestionAssignData
                                                                                ?.find(e => e.relatedComponentId === f.id)}
                                                                            error={submitMode ? errors[f.id]?.error : schemaErrors[f.id]}
                                                                            audience={audience}
                                                                            recipientType={recipientType}
                                                                            placeholderValues={formPlaceholders}
                                                                            getDraftDescriptionHtml={getDraftDescriptionHtml}
                                                                            setDraftDescriptionHtml={setDraftDescriptionHtml}
                                                                            commitDraftDescriptionToSchema={commitDraftDescriptionToSchema}
                                                                        />
                                                                    </Box>
                                                                </Box>
                                                            )}
                                                        </Draggable>
                                                    )
                                                    : null
                                            )}
                                            {provided.placeholder}
                                        </Box>)}
                                </Droppable>
                            </DragDropContext>
                            : (
                                <Box>
                                    {schema
                                        ?.filter(
                                            (e) => checkIfVisible(e.relates, submitData) && e.type !== 'schoolLogo'
                                        )
                                        .map((f, index) => {
                                            const hiddenTypes = ['intakeChoice', 'boarderStatusChoice'];

                                            const validOptionCount = f.options?.filter(
                                                o => o.value !== '' && o.value != null
                                            ).length ?? 0;

                                            const shouldHideVisually =
                                                hiddenTypes.includes(f.type) &&
                                                (validOptionCount === 0 || validOptionCount === 1);

                                            if (shouldHideVisually) {
                                                return null;
                                            }

                                            const isSeparator = f.type === 'separator';

                                            const content = (
                                                <ComponentBuilder
                                                    handleChangeSubmit={handleChangeSubmit}
                                                    handleDownloadAttachment={handleDownloadAttachment}
                                                    index={index}
                                                    readOnly={readOnly}
                                                    handleChangeEdit={handleChangeEdit}
                                                    previewMode={componentToEditIndex === index || previewMode}
                                                    formPreviewMode={previewMode}
                                                    field={f}
                                                    componentToEditIndex={componentToEditIndex}
                                                    data={data}
                                                    submitMode={submitMode}
                                                    submitData={submitData}
                                                    isFormArchived={isFormArchived}
                                                    placeholderValues={formPlaceholders}
                                                    error={submitMode ? errors[f.id]?.error : schemaErrors[f.id]}
                                                    getDraftDescriptionHtml={getDraftDescriptionHtml}
                                                    setDraftDescriptionHtml={setDraftDescriptionHtml}
                                                    commitDraftDescriptionToSchema={commitDraftDescriptionToSchema}
                                                />
                                            );

                                            return isSeparator
                                                ? (
                                                    <Box key={`formItem-${f.id}`}
                                                         sx={{
                                                             my: 7,
                                                             borderRadius: '8px',
                                                         }}>
                                                        {content}
                                                    </Box>
                                                )
                                                : (
                                                    <Box
                                                        key={`formItem-${f.id}`}
                                                        sx={{
                                                            my: 2,
                                                            px: 3,
                                                            py: 6,
                                                            border: '1px solid #d1d5db',
                                                            borderRadius: '8px',
                                                            backgroundColor: '#ffffff',
                                                            display: shouldHideVisually ? 'none' : 'block',
                                                        }}
                                                    >
                                                        {content}
                                                    </Box>
                                                );
                                        })}
                                </Box>
                            )
                        }
                        <Box
                            display={'flex'}
                            alignItems={'center'}
                            justifyContent={submitMode ? 'end' : 'start'}
                            gap={1}
                            pt={1}
                        >
                            {!previewMode && !isCommunication
                                ? <>
                                    <Stack spacing={2}>
                                        {Object.values(ComponentCategory).map((category) => {
                                            const items = formComponentsByCategory[category] || [];

                                            if (!items.length) return null;

                                            const isStudentProfileAttributes = category === ComponentCategory.STUDENT_PROFILE_ATTRIBUTES;

                                            return (
                                                <Box key={category}>
                                                    <Box sx={{
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        gap: 0.5,
                                                        mb: 1
                                                    }}>
                                                        <Typography variant="subtitle2" color="textSecondary">
                                                            {category}
                                                        </Typography>
                                                        {isStudentProfileAttributes && (
                                                            <Tooltip
                                                                title="When these attributes are added to a form and the form is linked with students, the respective values will be automatically updated on the student profile once the form is submitted."
                                                                placement="right"
                                                                arrow
                                                            >
                                                                <InfoOutlinedIcon
                                                                    fontSize="small"
                                                                    sx={{
                                                                        color: 'text.secondary',
                                                                        cursor: 'help'
                                                                    }}
                                                                />
                                                            </Tooltip>
                                                        )}
                                                    </Box>
                                                    <Stack direction="row" flexWrap="wrap" gap={1}>
                                                        {items.map(renderComponentButton)}
                                                    </Stack>
                                                </Box>
                                            );
                                        })}
                                    </Stack>
                                </>
                                : readOnly || previewMode || isCommunication
                                    ? null
                                    : <Button onClick={onSubmit}>Submit</Button>}
                        </Box>
                    </Box>
                    <AdmicityDialog
                        handleClose={handleDialogClose}
                        title={'Show Field When'}
                        actions={[]}
                        open={Boolean(currentComponent)}
                        maxWidth={'md'}
                    >
                        <ShowFieldWhen component={currentComponent} handleShowFieldWhen={handleShowFieldWhen}
                                       availableComponents={relatedComponents.filter(c => c.id !== currentComponent?.id)}/>
                    </AdmicityDialog>
                    <ConfirmRemoveDialog
                        open={Boolean(componentToConfirmRemove)}
                        handleClose={() => setComponentToConfirmRemove(undefined)}
                        handleConfirm={componentToConfirmRemove?.handleRemove}
                        relatedComponents={componentToConfirmRemove?.components}/>
                </Box>
            </Box>);
};

FormBuilder.propTypes = {
    schema: PropTypes.array,
    schemaId: PropTypes.number,
    onSchemaChange: PropTypes.func,
    onSchemaValidationChange: PropTypes.func,
    submitMode: PropTypes.bool,
    handleSubmit: PropTypes.func,
    handleDownloadAttachment: PropTypes.func,
    data: PropTypes.object,
    readOnly: PropTypes.bool,
    isFormArchived: PropTypes.bool,
    accessLevel: PropTypes.number,
    placeholderValues: PropTypes.object,
    formId: PropTypes.number,
    componentToEditIndex: PropTypes.number,
    setComponentToEditIndex: PropTypes.func,
    publicForm: PropTypes.bool,
    isCommunication: PropTypes.bool,
    audience: PropTypes.number,
    recipientType: PropTypes.string,
    formRequestId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    onEmailCheck: PropTypes.func,
    formAccessLevel: PropTypes.number,
    getDraftDescriptionHtml: PropTypes.func,
    setDraftDescriptionHtml: PropTypes.func,
    commitDraftDescriptionToSchema: PropTypes.func,

};

export default FormBuilder;